import React, { useEffect, useState } from 'react';
import { Button, Card, Col, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import Papa from 'papaparse';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import ListUploadSuccessModal from './ListUploadSuccessModal';

import {
  botBroadcastDetailsGetById,
  botBroadcastMappingList,
  botBroadcastUploadCsv,
  listMappingSelected,
  mappingListAdd,
  mappingListGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormInput from '../../../components/form/FormInput';

const BotBroadcastUpload = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigation = useNavigate();

  const [listDetails, setListDetails] = useState(null);
  const [fileName, setFileName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [mappingOptions, setMappingOptions] = useState([]);
  const [savedMapOptions, setSavedMapOptions] = useState([]);
  const [savedMapId, setSavedMapId] = useState(null);
  const [customMapName, setCustomMapName] = useState(null);
  const [csvData, setCsvData] = useState({ column: [], row: [] });
  const [selectedList, setSelectedList] = useState({});
  const [checkUnique, setCheckUnique] = useState(false);
  const [checkHeader, setCheckHeader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setFileName(file.name);
      setCsvFile(file);
    } else {
      toast.error('Please upload a CSV file.');
    }
  };

  const getListUploadDetails = async (id) => {
    try {
      const response = await dispatch(botBroadcastDetailsGetById(id)).unwrap();
      setListDetails(response?.data);
    } catch (error) {
      console.log('error', error);
    }
  };

  const addMappingList = async () => {
    try {
      const payload = {
        name: customMapName,
        data: selectedList,
        crm_id: listDetails?.crm_id
      };
      await dispatch(mappingListAdd(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchMappingList = async () => {
    try {
      const response = await dispatch(mappingListGet()).unwrap();
      if (response && response?.length) {
        const options = response?.map((x) => ({
          label: x.name,
          value: x.id
        }));
        setSavedMapOptions(options);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (id) {
      getListUploadDetails(id);
      fetchMappingList();
    }
  }, [id]);

  const selectedMapListFetch = async () => {
    try {
      const response = await dispatch(listMappingSelected(savedMapId)).unwrap();
      if (response && response.selected) {
        setSelectedList(response?.selected);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (savedMapId) {
      selectedMapListFetch();
    }
  }, [savedMapId]);

  const getMappingListOption = async () => {
    try {
      const response = await dispatch(botBroadcastMappingList(listDetails?.crm_id)).unwrap();

      if (response) {
        const defaultFields = response?.default_fields;
        const crmsFields = response?.crms_fieds;
        const mappingFields = response?.mapping_fields;
        const options = [];
        if (defaultFields && Object.keys(defaultFields).length > 0) {
          const defaultFieldOptions = Object.entries(defaultFields).map(([key, value]) => ({
            label: value,
            value: key
          }));

          options.push({
            label: 'General Fields',
            options: defaultFieldOptions
          });
        }

        if (mappingFields && Object.keys(mappingFields).length > 0) {
          const customFieldOptions = Object.entries(mappingFields).map(([key, value]) => ({
            label: value,
            value: key
          }));

          options.push({
            label: 'Mapping Fields',
            options: customFieldOptions
          });
        }

        if (crmsFields && Object.keys(crmsFields).length > 0) {
          const customFieldOptions = Object.entries(crmsFields).map(([key, value]) => ({
            label: value,
            value: key
          }));

          options.push({
            label: 'CRM Fields',
            options: customFieldOptions
          });
        }
        setMappingOptions(options);
      } else {
        setMappingOptions([]);
      }
    } catch (error) {
      setMappingOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (listDetails && listDetails?.crm_id) {
      getMappingListOption();
    }
  }, [listDetails]);

  const uploadFile = () => {
    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = function (e) {
      const text = e.target.result;
      const parsedData = Papa.parse(text, {
        header: false,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim()
      });
      const columns = parsedData.data[0]; // First row is the column names
      let rows = parsedData.data.slice(1); // The rest are the data rows
      if (rows.length > 2) {
        rows = rows.slice(0, 2);
      }
      setCsvData({ column: [...columns], row: [...rows] });
      setIsLoading(false);
    };

    reader.readAsText(csvFile);
  };

  const handleMappingChange = (index, value) => {
    setSelectedList((prevState) => ({
      ...prevState,
      [index]: value
    }));
  };

  const onUploadCsv = async () => {
    setLoading(true);
    if (customMapName) {
      addMappingList();
    }
    try {
      const formData = new FormData();
      formData.append('crm_id', listDetails?.crm_id);
      formData.append('whatsapp_bot_broadcast_id', listDetails?.id);
      formData.append('check_unique', checkUnique);
      formData.append('check_header', checkHeader);
      formData.append('selected_list', JSON.stringify(selectedList));
      formData.append('file', csvFile);
      const response = await dispatch(botBroadcastUploadCsv(formData)).unwrap();
      if (response) {
        setLoading(false);
        setSuccessDetails(response);
        setIsModalOpen(true);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log('error', error);
    }
  };

  return (
    <div>
      <AdminHeader title="Bot Broadcast List Upload" />
      <Card className="m-0">
        {csvData?.column?.length > 0 ? (
          <Row className="p-3">
            <Col lg="3" md="3">
              <FormSelect
                value={savedMapId}
                name="savedMapId"
                isShowLabel={false}
                placeholder="Select Saved Mapping"
                options={savedMapOptions}
                onChange={(e) => setSavedMapId(e.target.value)}
              />
            </Col>
            <Col lg="3" md="3">
              <FormInput
                placeholder="Add Custom Mapping Name"
                isShowLabel={false}
                name="customMapName"
                value={customMapName}
                onChange={(e) => setCustomMapName(e.target.value)}
              />
            </Col>
            <Col
              lg="6"
              md="6"
              className="d-flex flex-row align-items-baseline justify-content-start gap-4">
              <Button
                className="px-5 bg-transparent text-primary"
                onClick={() => {
                  navigation(-1, { replace: true });
                  setListDetails(null);
                  setSelectedList({});
                  setCheckUnique(false);
                  setFileName(null);
                  setCsvFile(null);
                  setCsvData({ column: [], row: [] });
                }}>
                Cancel
              </Button>
              <Button
                className="px-5 bg-primary border-primary d-flex justify-content-center align-items-center gap-2"
                disabled={loading || !csvFile || isLoading}
                onClick={() => onUploadCsv()}>
                Submit
                {loading ? <Spinner size="sm" color="light" /> : null}
              </Button>
            </Col>
          </Row>
        ) : (
          <div className="p-3 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-row">
              <div className="me-3">Broadcast Name :</div>
              <div className="text-dark fw-bold">{listDetails?.name}</div>
            </div>
            <div className="d-flex flex-row">
              <div className="me-3">CRM Name :</div>
              <div className="text-dark fw-bold">{listDetails?.crm_name}</div>
            </div>
            <div className="d-flex flex-row">
              <div className="me-2">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      id="check_unique"
                      checked={checkUnique}
                      onChange={() => setCheckUnique(!checkUnique)}
                    />
                  </Label>
                </FormGroup>
              </div>
              <div className="text-dark fw-bold">Check Unique</div>
            </div>
            <div className="d-flex flex-row">
              <div className="me-2">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      id="check_header"
                      checked={checkHeader}
                      onChange={() => setCheckHeader(!checkHeader)}
                    />
                  </Label>
                </FormGroup>
              </div>
              <div className="text-dark fw-bold">Check Header</div>
            </div>
            <div className="file-upload-container">
              <Input
                type="text"
                className="file-name"
                placeholder={fileName ? fileName : 'Choose file'}
                readOnly
              />
              <label className="upload-button">
                <Icon.Upload size={18} />
                <Input
                  type="file"
                  id="file-upload"
                  className="file-upload"
                  accept=".csv"
                  onChange={handleFileChange}
                />
              </label>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                className="px-3 bg-transparent text-primary"
                onClick={() => {
                  navigation(-1, { replace: true });
                  setListDetails(null);
                  setSelectedList({});
                  setCheckUnique(false);
                  setFileName(null);
                  setCsvFile(null);
                  setCsvData({ column: [], row: [] });
                }}>
                Cancel
              </Button>
              <Button
                className="px-3 bg-primary text-white"
                disabled={!csvFile}
                onClick={() => uploadFile()}>
                Upload
              </Button>
            </div>
          </div>
        )}

        <div
          className="daily-report-table"
          style={{ height: 'calc(100vh - 250px)', overflow: 'auto' }}>
          {isLoading ? (
            <div className="h-100 w-100 d-flex justify-content-center align-items-center">
              <CallexLoader />
            </div>
          ) : csvData?.column?.length > 0 ? (
            <table className="report-list-table">
              <thead>
                <tr>
                  <th style={{ width: '300px' }} className="mapping-col">
                    Mapping
                  </th>
                  <th>Row 1</th>
                  <th>Row 2</th>
                  <th>Row 3</th>
                </tr>
              </thead>
              <tbody>
                {csvData?.column.map((col, colIndex) => (
                  <tr key={colIndex}>
                    <td className="p-1">
                      <FormSelect
                        className="table-select"
                        isShowLabel={false}
                        placeholder="Select Field"
                        value={selectedList[colIndex] || ''}
                        options={mappingOptions.map((group) => ({
                          label: group.label,
                          options: group.options.filter(
                            (option) =>
                              !Object.values(selectedList).includes(option.value) ||
                              selectedList[colIndex] === option.value
                          )
                        }))}
                        onChange={(e) => handleMappingChange(colIndex, e.target.value)}
                        isGroup
                        setPlacementAuto
                      />
                    </td>
                    <td>{col}</td>
                    {csvData?.row.map((row, rowIndex) => (
                      <td key={rowIndex}>{row[colIndex] || '-'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="h-100 w-100 d-flex justify-content-center align-items-center no-data">
              Please select file
            </div>
          )}
        </div>
        {/* <CardFooter className="d-flex justify-content-end gap-3">
          
        </CardFooter> */}
      </Card>
      <ListUploadSuccessModal
        isOpen={isModalOpen}
        recordDetails={successDetails}
        onDoneClick={() => {
          navigation(-1, { replace: true });
          setListDetails(null);
          setSelectedList({});
          setCheckUnique(false);
          setIsModalOpen(false);
          setSuccessDetails(null);
          setFileName(null);
          setCsvFile(null);
          setCsvData({ column: [], row: [] });
        }}
      />
    </div>
  );
};

export default BotBroadcastUpload;
